import { ArrowRight, BadgeCheck, MailCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Input, Select } from "../../design-system";
import { apiFetch, errorMessage } from "../../lib/api";

export function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"register" | "verify">("register");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsVersion, setTermsVersion] = useState("terms-2026-01");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    partnerType: "PERSONAL_TRAINER",
  });
  const [code, setCode] = useState("");

  useEffect(() => {
    apiFetch<{ termsDocumentVersion: string }>("/auth/config", {
      skipRefresh: true,
    })
      .then((data) => setTermsVersion(data.termsDocumentVersion))
      .catch(() => undefined);
  }, []);

  async function register(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await apiFetch<{ requiresEmailVerification: boolean }>(
        "/auth/partners/register",
        {
          method: "POST",
          body: {
            ...form,
            phone: form.phone || undefined,
            acceptedTermsVersion: termsVersion,
          },
          skipRefresh: true,
        },
      );
      if (result.requiresEmailVerification) {
        setStep("verify");
      } else {
        navigate("/parceiro/entrar", {
          replace: true,
          state: { registered: true, email: form.email },
        });
      }
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await apiFetch("/auth/partners/verify-email", {
        method: "POST",
        body: { email: form.email, code },
        skipRefresh: true,
      });
      navigate("/parceiro/entrar", {
        replace: true,
        state: { verified: true },
      });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  if (step === "verify") {
    return (
      <div className="auth-form-card">
        <span className="auth-role-icon auth-role-icon--success">
          <MailCheck />
        </span>
        <span className="eyebrow">Última etapa</span>
        <h2>Confira seu e-mail</h2>
        <p>
          Enviamos um código de 6 dígitos para <strong>{form.email}</strong>.
        </p>
        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}
        <form onSubmit={verify} className="form-stack">
          <Input
            label="Código de verificação"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            className="input input--otp"
          />
          <Button type="submit" loading={loading}>
            Validar minha conta <BadgeCheck size={18} />
          </Button>
        </form>
        <button className="text-button" onClick={() => setStep("register")}>
          Corrigir meu e-mail
        </button>
      </div>
    );
  }

  return (
    <div className="auth-form-card auth-form-card--wide">
      <span className="eyebrow">Faça parte da rede</span>
      <h2>Crie sua conta de indicador</h2>
      <p>
        Leva menos de dois minutos. Depois, você receberá seu link exclusivo.
      </p>
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}
      <form onSubmit={register} className="form-grid">
        <Input
          label="Nome completo"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Seu nome"
        />
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="voce@exemplo.com"
        />
        <Input
          label="Telefone"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="(11) 99999-9999"
        />
        <Select
          label="Tipo de parceria"
          value={form.partnerType}
          onChange={(e) => setForm({ ...form, partnerType: e.target.value })}
        >
          <option value="PERSONAL_TRAINER">Personal trainer</option>
          <option value="PROFESSIONAL">Profissional parceiro</option>
          <option value="INFLUENCER">Influenciador(a)</option>
          <option value="OTHER">Outro</option>
        </Select>
        <div className="form-grid__full">
          <Input
            label="Crie uma senha"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            hint="Use pelo menos 10 caracteres."
          />
        </div>
        <label className="check-row form-grid__full">
          <input type="checkbox" required />
          <span>
            Li e aceito os termos de participação e a política de privacidade
            vigentes.
          </span>
        </label>
        <div className="form-grid__full">
          <Button type="submit" loading={loading}>
            Criar minha conta <ArrowRight size={18} />
          </Button>
        </div>
      </form>
      <p className="auth-switch">
        Já tem uma conta? <Link to="/parceiro/entrar">Entrar agora</Link>
      </p>
    </div>
  );
}
